import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Wallet, Building, Send, CheckCircle2, AlertCircle, History, TrendingUp, DollarSign } from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_VARIANT = {
  pending: 'outline',
  payout_requested: 'secondary',
  paid: 'default',
  rejected: 'destructive',
  clawed_back: 'destructive'
};

export default function AffiliatePayouts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSubaccountForm, setShowSubaccountForm] = useState(false);

  const [sub, setSub] = useState({
    business_name: '',
    settlement_bank: '',
    account_number: '',
    primary_contact_email: '',
    primary_contact_phone: ''
  });

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/referrals/affiliates/payout-dashboard`);
      setData(res.data);
    } catch (err) {
      console.error('Error loading payout dashboard:', err);
      toast.error(err.response?.data?.detail || 'Failed to load payout dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleCreateSubaccount = async (e) => {
    e.preventDefault();
    if (!sub.business_name || !sub.settlement_bank || !sub.account_number) {
      toast.error('Business name, bank code, and account number are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/referrals/affiliates/create-subaccount`, sub);
      toast.success(res.data.message || 'Subaccount created');
      setShowSubaccountForm(false);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create subaccount');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!data?.eligible_commissions?.length) {
      toast.error('No eligible commissions to request');
      return;
    }
    if (!data?.has_subaccount) {
      toast.error('Please set up your payout bank account first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/referrals/affiliates/request-payout`, {
        commission_ids: data.eligible_commissions.map(c => c.commission_id)
      });
      toast.success(res.data.message || 'Payout requested');
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request payout');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-muted-foreground" data-testid="payouts-loading">Loading payout dashboard…</div>;
  }

  if (!data) {
    return <div className="text-center py-12 text-muted-foreground">Unable to load dashboard. Refresh and try again.</div>;
  }

  const currency = data.eligible_commissions?.[0]?.currency || data.payout_requests?.[0]?.currency || 'NGN';

  return (
    <div className="space-y-6" data-testid="affiliate-payouts-tab">
      {/* Headline Cards: Total Sales + Total Earnings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-cyan-500/10"><TrendingUp className="w-6 h-6 text-cyan-500" /></div>
              <div>
                <p className="text-3xl font-bold" data-testid="payout-total-sales">
                  {currency} {(data.total_sales || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Total Sales <span className="opacity-70">— gross value of tickets sold via your links</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-emerald-500/10"><DollarSign className="w-6 h-6 text-emerald-500" /></div>
              <div>
                <p className="text-3xl font-bold" data-testid="payout-total-earnings">
                  {currency} {(data.total_earnings || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Total Earnings <span className="opacity-70">— your cut across all sales</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10"><Wallet className="w-5 h-5 text-amber-500" /></div>
              <div>
                <p className="text-2xl font-bold" data-testid="payout-pending-balance">
                  {(data.pending_balance || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Available <span className="opacity-70">(earned − withdrawn)</span></p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10"><CheckCircle2 className="w-5 h-5 text-green-500" /></div>
              <div>
                <p className="text-2xl font-bold" data-testid="payout-paid-balance">
                  {(data.paid_balance || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">Withdrawn</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10"><Send className="w-5 h-5 text-blue-500" /></div>
              <div>
                <p className="text-2xl font-bold" data-testid="payout-eligible-total">
                  {(data.eligible_total || 0).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  Eligible Now ({data.eligible_commissions?.length || 0} sales)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Subaccount Setup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Building className="w-5 h-5" /> Payout Bank Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.has_subaccount && data.subaccount ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="font-medium">{data.subaccount.business_name}</span>
              </div>
              <p className="text-sm text-muted-foreground" data-testid="payout-subaccount-info">
                {data.subaccount.account_name || '—'} | Account: ••••{(data.subaccount.account_number || '').slice(-4)} | Bank: {data.subaccount.settlement_bank}
              </p>
              <p className="text-xs text-muted-foreground">
                Subaccount: <code>{data.subaccount.subaccount_code}</code>
              </p>
            </div>
          ) : showSubaccountForm ? (
            <form onSubmit={handleCreateSubaccount} className="space-y-3" data-testid="subaccount-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="biz">Business / Account Holder Name *</Label>
                  <Input id="biz" value={sub.business_name} onChange={(e) => setSub({ ...sub, business_name: e.target.value })} required data-testid="subaccount-business-name" />
                </div>
                <div>
                  <Label htmlFor="bank">Bank Code *</Label>
                  <Input id="bank" value={sub.settlement_bank} onChange={(e) => setSub({ ...sub, settlement_bank: e.target.value })} placeholder="e.g. 058 for GTBank" required data-testid="subaccount-bank-code" />
                </div>
                <div>
                  <Label htmlFor="acc">Account Number *</Label>
                  <Input id="acc" value={sub.account_number} onChange={(e) => setSub({ ...sub, account_number: e.target.value })} placeholder="10-digit account number" required data-testid="subaccount-account-number" />
                </div>
                <div>
                  <Label htmlFor="phone">Contact Phone</Label>
                  <Input id="phone" value={sub.primary_contact_phone} onChange={(e) => setSub({ ...sub, primary_contact_phone: e.target.value })} placeholder="+2348..." data-testid="subaccount-contact-phone" />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="cemail">Contact Email</Label>
                  <Input id="cemail" type="email" value={sub.primary_contact_email} onChange={(e) => setSub({ ...sub, primary_contact_email: e.target.value })} data-testid="subaccount-contact-email" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={submitting} data-testid="subaccount-submit-btn">
                  {submitting ? 'Creating…' : 'Create Subaccount'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowSubaccountForm(false)}>Cancel</Button>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Bank code reference (NG): GTBank=058, Access=044, UBA=033, First Bank=011, Zenith=057, Sterling=232. Use bank.resolve from Paystack to verify.
              </p>
            </form>
          ) : (
            <div className="text-center py-6 space-y-3">
              <AlertCircle className="w-10 h-10 mx-auto text-amber-500 opacity-80" />
              <p className="text-sm text-muted-foreground">
                Set up a Paystack subaccount to receive payouts. Payouts are processed by the platform admin after review.
              </p>
              <Button onClick={() => setShowSubaccountForm(true)} data-testid="setup-subaccount-btn">
                Set up Payout Account
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Eligible Commissions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2"><Send className="w-5 h-5" /> Eligible for Payout</span>
            <Button
              size="sm"
              disabled={!data.eligible_commissions?.length || !data.has_subaccount || submitting}
              onClick={handleRequestPayout}
              data-testid="request-payout-btn"
            >
              Request Payout ({currency} {(data.eligible_total || 0).toLocaleString()})
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data.eligible_commissions?.length ? (
            <p className="text-center text-muted-foreground text-sm py-6">
              No commissions eligible right now. Earn commissions by sharing your referral links.
            </p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-left p-3 font-medium">Event</th>
                    <th className="text-right p-3 font-medium">Sale</th>
                    <th className="text-center p-3 font-medium">Rate</th>
                    <th className="text-right p-3 font-medium">Commission</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.eligible_commissions.map(c => (
                    <tr key={c.commission_id} data-testid={`eligible-commission-${c.commission_id}`}>
                      <td className="p-3 text-xs">{new Date(c.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-xs font-mono">
                        {c.event_id?.slice(0, 12)}…
                        {c.assisted_purchase && (
                          <Badge variant="secondary" className="ml-2 text-[10px] font-sans" data-testid={`assisted-badge-${c.commission_id}`}>Assisted</Badge>
                        )}
                      </td>
                      <td className="p-3 text-right">{c.currency} {(c.ticket_sale_amount || 0).toLocaleString()}</td>
                      <td className="p-3 text-center">{c.commission_percent}%</td>
                      <td className="p-3 text-right font-medium">{c.currency} {(c.commission_amount || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Request History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="w-5 h-5" /> Payout Request History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data.payout_requests?.length ? (
            <p className="text-center text-muted-foreground text-sm py-6">
              No payout requests yet.
            </p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-left p-3 font-medium">Request ID</th>
                    <th className="text-center p-3 font-medium">Items</th>
                    <th className="text-right p-3 font-medium">Amount</th>
                    <th className="text-center p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.payout_requests.map(p => (
                    <tr key={p.request_id} data-testid={`payout-row-${p.request_id}`}>
                      <td className="p-3 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                      <td className="p-3 text-xs font-mono">{p.request_id}</td>
                      <td className="p-3 text-center">{p.commission_ids?.length || 0}</td>
                      <td className="p-3 text-right font-medium">{p.currency} {(p.total_amount || 0).toLocaleString()}</td>
                      <td className="p-3 text-center">
                        <Badge variant={STATUS_VARIANT[p.status] || 'outline'}>{p.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
